import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Scissors, CalendarCheck, LogOut, Plus, Pencil, Trash2,
  TrendingUp, Clock3, IndianRupee, Sparkles, Images, Settings, Tag, UserX,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import ContentManager from "@/pages/admin/AdminContent";
import SettingsManager from "@/pages/admin/AdminSettings";
import PriceManager from "@/pages/admin/AdminPrices";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const STATUSES = ["Pending", "Confirmed", "Paid", "In Progress", "Completed", "Cancelled", "No Show"];
const CATEGORIES = ["Hair", "Facial & Skin", "Threading", "Waxing", "Manicure & Pedicure", "Makeup", "Mehendi", "Other"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [content, setContent] = useState({ offers: [], packages: [], gallery: [], reviews: [] });
  const [settings, setSettings] = useState({});
  const [deletions, setDeletions] = useState([]);
  const [editor, setEditor] = useState(null); // service being edited, {} = new

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, sv, b, of, pk, gl, rv, st, del] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/services", { params: { all: true } }),
        api.get("/bookings"),
        api.get("/offers"),
        api.get("/packages"),
        api.get("/gallery"),
        api.get("/admin/reviews"),
        api.get("/settings"),
        api.get("/deletion-requests"),
      ]);
      setStats(s.data);
      setServices(sv.data);
      setBookings(b.data);
      setContent({ offers: of.data, packages: pk.data, gallery: gl.data, reviews: rv.data });
      setSettings(st.data || {});
      setDeletions(del.data || []);
    } catch {
      // handled by auth redirect
    }
  }, []);

  useEffect(() => {
    if (user && user.role) loadAll();
  }, [user, loadAll]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const saveService = async (form) => {
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        discount_price: form.discount_price === "" || form.discount_price == null ? null : Number(form.discount_price),
        duration: Number(form.duration),
      };
      if (form.id) await api.put(`/services/${form.id}`, payload);
      else await api.post("/services", payload);
      toast.success(form.id ? "Service updated" : "Service added");
      setEditor(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/services/${id}`);
    toast.success("Service deleted");
    loadAll();
  };

  const updateBooking = async (id, status) => {
    await api.put(`/bookings/${id}`, { status });
    toast.success("Booking updated");
    loadAll();
  };

  if (!user || !user.role) {
    return <div className="min-h-screen grid place-items-center bg-[#FAFAF8] text-[#5C4A4D]">Loading…</div>;
  }

  const NAV = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "services", label: "Services", icon: Scissors },
    { id: "prices", label: "Service Prices", icon: Tag },
    { id: "bookings", label: "Bookings", icon: CalendarCheck },
    { id: "content", label: "Content", icon: Images },
    { id: "settings", label: "Settings", icon: Settings },
    { id: "deletions", label: "Deletion Requests", icon: UserX },
  ];

  return (
    <div className="min-h-screen flex bg-[#FAFAF8] font-[Manrope]">
      {/* Sidebar */}
      <aside className="w-[250px] shrink-0 bg-[#2A1B1D] text-[#FDFBF7] flex flex-col fixed h-screen">
        <div className="px-6 py-7 border-b border-[#FDFBF7]/10">
          <p className="font-display text-3xl">Munmun</p>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37] mt-1">Admin Panel</p>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              data-testid={`nav-${n.id}`}
              className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                tab === n.id ? "bg-[#8E3B46] text-[#FDFBF7]" : "text-[#FDFBF7]/70 hover:bg-[#FDFBF7]/5"
              }`}
            >
              <n.icon size={18} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="px-4 py-6 border-t border-[#FDFBF7]/10">
          <p className="px-4 text-xs text-[#FDFBF7]/50 mb-3 truncate">{user.email}</p>
          <button onClick={handleLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-[#FDFBF7]/70 hover:bg-[#FDFBF7]/5 transition-colors">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-[250px] p-8 lg:p-10">
        {tab === "dashboard" && <DashboardView stats={stats} bookings={bookings} />}
        {tab === "services" && (
          <ServicesView services={services} onAdd={() => setEditor({})} onEdit={setEditor} onDelete={deleteService} />
        )}
        {tab === "prices" && (
          <PriceManager services={services} reload={loadAll} onAdd={() => setEditor({})} />
        )}
        {tab === "bookings" && <BookingsView bookings={bookings} onUpdate={updateBooking} />}
        {tab === "content" && (
          <ContentManager offers={content.offers} packages={content.packages} gallery={content.gallery} reviews={content.reviews} reload={loadAll} />
        )}
        {tab === "settings" && <SettingsManager settings={settings} reload={loadAll} />}
        {tab === "deletions" && <DeletionsView deletions={deletions} reload={loadAll} />}
      </main>

      {editor && <ServiceEditor initial={editor} onClose={() => setEditor(null)} onSave={saveService} />}
    </div>
  );
}

/* ---------- Dashboard ---------- */
function DashboardView({ stats, bookings }) {
  const cards = [
    { label: "Total Bookings", value: stats.total_bookings ?? 0, icon: CalendarCheck },
    { label: "Today's Bookings", value: stats.today_bookings ?? 0, icon: Clock3 },
    { label: "Pending", value: stats.pending ?? 0, icon: TrendingUp },
    { label: "Revenue", value: inr(stats.revenue), icon: IndianRupee },
    { label: "Services", value: stats.services ?? 0, icon: Sparkles },
  ];
  return (
    <div>
      <h1 className="font-display text-4xl text-[#2A1B1D]">Dashboard</h1>
      <p className="text-sm text-[#5C4A4D] mt-1">Overview of your parlour at a glance.</p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 mt-8">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-white border border-[#2A1B1D]/8 p-5 shadow-[0_8px_32px_rgba(42,27,29,0.04)]" data-testid={`stat-${c.label}`}>
            <c.icon className="text-[#8E3B46]" size={20} />
            <p className="font-display text-3xl text-[#2A1B1D] mt-3">{c.value}</p>
            <p className="text-xs text-[#5C4A4D] mt-1">{c.label}</p>
          </div>
        ))}
      </div>
      <h2 className="font-display text-2xl text-[#2A1B1D] mt-12 mb-4">Recent Bookings</h2>
      <div className="rounded-2xl bg-white border border-[#2A1B1D]/8 overflow-hidden">
        <BookingsTable bookings={bookings.slice(0, 6)} />
      </div>
    </div>
  );
}

/* ---------- Services ---------- */
function ServicesView({ services, onAdd, onEdit, onDelete }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl text-[#2A1B1D]">Services</h1>
          <p className="text-sm text-[#5C4A4D] mt-1">Prices update instantly on the website.</p>
        </div>
        <button onClick={onAdd} data-testid="add-service-btn" className="inline-flex items-center gap-2 rounded-full bg-[#8E3B46] px-5 py-3 text-sm font-medium text-[#FDFBF7] hover:bg-[#722F38] transition-colors">
          <Plus size={16} /> Add Service
        </button>
      </div>
      <div className="mt-8 rounded-2xl bg-white border border-[#2A1B1D]/8 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#5C4A4D] bg-[#F4EFEA]/60">
              <th className="px-5 py-3">Service</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Price</th>
              <th className="px-5 py-3">Discount</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} className="border-t border-[#2A1B1D]/6" data-testid={`admin-service-${s.id}`}>
                <td className="px-5 py-4 font-medium text-[#2A1B1D]">{s.name}</td>
                <td className="px-5 py-4 text-[#5C4A4D]">{s.category}</td>
                <td className="px-5 py-4 text-[#2A1B1D]">{inr(s.price)}</td>
                <td className="px-5 py-4 text-[#8E3B46]">{s.discount_price != null ? inr(s.discount_price) : "—"}</td>
                <td className="px-5 py-4">
                  <span className={`rounded-full px-2.5 py-1 text-xs ${s.status ? "bg-[#3E664E]/10 text-[#3E664E]" : "bg-[#8E3B46]/10 text-[#8E3B46]"}`}>
                    {s.status ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => onEdit(s)} data-testid={`edit-service-${s.id}`} className="p-2 rounded-lg hover:bg-[#F4EFEA] text-[#5C4A4D]"><Pencil size={15} /></button>
                    <button onClick={() => onDelete(s.id)} data-testid={`delete-service-${s.id}`} className="p-2 rounded-lg hover:bg-[#8E3B46]/10 text-[#8E3B46]"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Bookings ---------- */
function BookingsView({ bookings, onUpdate }) {
  return (
    <div>
      <h1 className="font-display text-4xl text-[#2A1B1D]">Bookings</h1>
      <p className="text-sm text-[#5C4A4D] mt-1">Manage appointment requests and statuses.</p>
      <div className="mt-8 rounded-2xl bg-white border border-[#2A1B1D]/8 overflow-hidden">
        <BookingsTable bookings={bookings} onUpdate={onUpdate} />
      </div>
    </div>
  );
}

function BookingsTable({ bookings, onUpdate }) {
  if (!bookings.length) {
    return <div className="p-8 text-center text-sm text-[#5C4A4D]">No bookings yet.</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-[#5C4A4D] bg-[#F4EFEA]/60">
          <th className="px-5 py-3">Ref</th>
          <th className="px-5 py-3">Customer</th>
          <th className="px-5 py-3">Service</th>
          <th className="px-5 py-3">Date · Time</th>
          <th className="px-5 py-3">Amount</th>
          <th className="px-5 py-3">Status</th>
        </tr>
      </thead>
      <tbody>
        {bookings.map((b) => (
          <tr key={b.id} className="border-t border-[#2A1B1D]/6" data-testid={`admin-booking-${b.id}`}>
            <td className="px-5 py-4 font-mono-x text-xs text-[#8E3B46]">{b.booking_ref}</td>
            <td className="px-5 py-4">
              <p className="font-medium text-[#2A1B1D]">{b.name}</p>
              <p className="text-xs text-[#5C4A4D]">{b.phone}</p>
            </td>
            <td className="px-5 py-4 text-[#2A1B1D]">{b.service_name}</td>
            <td className="px-5 py-4 text-[#5C4A4D]">{b.date} · {b.time}</td>
            <td className="px-5 py-4 text-[#2A1B1D]">{inr(b.price_charged)}</td>
            <td className="px-5 py-4">
              {onUpdate ? (
                <select
                  value={b.status}
                  onChange={(e) => onUpdate(b.id, e.target.value)}
                  data-testid={`booking-status-${b.id}`}
                  className="rounded-lg border border-[#2A1B1D]/12 bg-white px-2 py-1.5 text-xs outline-none focus:border-[#D4AF37]"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <span className="rounded-full bg-[#F4EFEA] px-2.5 py-1 text-xs text-[#5C4A4D]">{b.status}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------- Service editor ---------- */
function ServiceEditor({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || "",
    category: initial.category || CATEGORIES[0],
    description: initial.description || "",
    image: initial.image || "",
    duration: initial.duration ?? 60,
    price: initial.price ?? "",
    discount_price: initial.discount_price ?? "",
    status: initial.status ?? true,
    featured: initial.featured ?? false,
    popular: initial.popular ?? false,
    order: initial.order ?? 0,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cls = "w-full rounded-xl border border-[#2A1B1D]/12 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#D4AF37]";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#FDFBF7] border border-[#D4AF37]/30 p-0 gap-0" data-testid="service-editor">
        <DialogTitle className="sr-only">{form.id ? "Edit Service" : "Add Service"}</DialogTitle>
        <DialogDescription className="sr-only">Manage service details, pricing and visibility.</DialogDescription>
        <div className="px-7 pt-7 pb-4 border-b border-[#D4AF37]/20">
          <h3 className="font-display text-3xl text-[#2A1B1D]">{form.id ? "Edit Service" : "Add Service"}</h3>
        </div>
        <div className="px-7 py-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <Fld label="Service name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className={cls} data-testid="svc-name" /></Fld>
          <Fld label="Category">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className={cls} data-testid="svc-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Fld>
          <Fld label="Description"><textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className={cls} data-testid="svc-desc" /></Fld>
          <Fld label="Image URL"><input value={form.image} onChange={(e) => set("image", e.target.value)} className={cls} data-testid="svc-image" placeholder="https://..." /></Fld>
          <div className="grid grid-cols-3 gap-3">
            <Fld label="Duration (min)"><input type="number" value={form.duration} onChange={(e) => set("duration", e.target.value)} className={cls} data-testid="svc-duration" /></Fld>
            <Fld label="Price (₹)"><input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className={cls} data-testid="svc-price" /></Fld>
            <Fld label="Discount (₹)"><input type="number" value={form.discount_price} onChange={(e) => set("discount_price", e.target.value)} className={cls} data-testid="svc-discount" placeholder="optional" /></Fld>
          </div>
          <div className="flex flex-wrap gap-5 pt-1">
            <Toggle label="Active" checked={form.status} onChange={(v) => set("status", v)} testid="svc-status" />
            <Toggle label="Featured" checked={form.featured} onChange={(v) => set("featured", v)} testid="svc-featured" />
            <Toggle label="Popular" checked={form.popular} onChange={(v) => set("popular", v)} testid="svc-popular" />
          </div>
        </div>
        <div className="px-7 py-5 border-t border-[#D4AF37]/20 flex justify-end gap-3 bg-[#F4EFEA]/60">
          <button onClick={onClose} className="rounded-full border border-[#2A1B1D]/15 px-6 py-2.5 text-sm text-[#2A1B1D]">Cancel</button>
          <button onClick={() => onSave(form)} data-testid="svc-save" className="rounded-full bg-[#8E3B46] px-7 py-2.5 text-sm font-medium text-[#FDFBF7] hover:bg-[#722F38] transition-colors">Save Service</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Deletion Requests ---------- */
function DeletionsView({ deletions, reload }) {
  const process = async (r) => {
    if (!window.confirm(`Process this request? All bookings for ${r.phone || r.email} will be permanently deleted.`)) return;
    try {
      const { data } = await api.put(`/deletion-requests/${r.id}`, { status: "Processed" });
      toast.success(`Processed — ${data.deleted_bookings} booking record(s) permanently deleted`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this request record?")) return;
    await api.delete(`/deletion-requests/${r.id}`);
    toast.success("Request removed");
    reload();
  };

  return (
    <div>
      <h1 className="font-display text-4xl text-[#2A1B1D]">Deletion Requests</h1>
      <p className="text-sm text-[#5C4A4D] mt-1">Account &amp; data deletion requests submitted via the public Delete Account page.</p>
      <div className="mt-8 rounded-2xl bg-white border border-[#2A1B1D]/8 overflow-hidden">
        {deletions.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#5C4A4D]">No deletion requests.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#5C4A4D] bg-[#F4EFEA]/60">
                <th className="px-5 py-3">Ref</th>
                <th className="px-5 py-3">Customer</th>
                <th className="px-5 py-3">Login Method</th>
                <th className="px-5 py-3">Reason</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {deletions.map((r) => (
                <tr key={r.id} className="border-t border-[#2A1B1D]/6" data-testid={`deletion-row-${r.id}`}>
                  <td className="px-5 py-4 font-mono-x text-xs text-[#8E3B46]">{r.request_ref}</td>
                  <td className="px-5 py-4">
                    <p className="font-medium text-[#2A1B1D]">{r.name}</p>
                    <p className="text-xs text-[#5C4A4D]">{r.phone} {r.email && `· ${r.email}`}</p>
                  </td>
                  <td className="px-5 py-4 text-[#5C4A4D]">{r.login_method || "—"}</td>
                  <td className="px-5 py-4 text-[#5C4A4D] max-w-[200px] truncate">{r.reason || "—"}</td>
                  <td className="px-5 py-4 text-[#5C4A4D]">{new Date(r.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="px-5 py-4">
                    <span className={`rounded-full px-2.5 py-1 text-xs ${r.status === "Processed" ? "bg-[#3E664E]/10 text-[#3E664E]" : "bg-[#D4AF37]/15 text-[#8a6d1a]"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {r.status !== "Processed" && (
                        <button onClick={() => process(r)} data-testid={`process-deletion-${r.id}`} className="rounded-full bg-[#8E3B46] px-4 py-1.5 text-xs font-medium text-[#FDFBF7] hover:bg-[#722F38] transition-colors">
                          Process &amp; Delete Data
                        </button>
                      )}
                      <button onClick={() => remove(r)} data-testid={`remove-deletion-${r.id}`} className="p-2 rounded-lg hover:bg-[#8E3B46]/10 text-[#8E3B46]"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid} className="flex items-center gap-2 text-sm text-[#2A1B1D]">
      <span className={`h-5 w-9 rounded-full transition-colors relative ${checked ? "bg-[#8E3B46]" : "bg-[#2A1B1D]/15"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
      </span>
      {label}
    </button>
  );
}
